import type {PosterOp, SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';

/** One commit as this tab sends it to the server. */
export interface LocalOpEnvelope {
  opId: string;
  sessionId: string;
  posterVersion: number;
  ops: PosterOp[];
}

/** An envelope as it goes to the server: whether the tab had company is decided when it is sent, not when it was built. */
export interface OutgoingOpEnvelope extends LocalOpEnvelope {
  isShared: boolean;
}

/** One commit as the server ordered it and every tab receives it. */
export interface PublishedOpEnvelope extends LocalOpEnvelope {
  seq: number;
  userId: number;
}

/** The response of stream/publishPosterOps. */
export interface PublishOpsResponse {
  seq: number;
  isDuplicate: boolean;
}

/** The response of stream/getPosterOps. */
export interface FetchOpsResponse {
  envelopes: PublishedOpEnvelope[];
}

/** The response of stream/flushPosterOps: the sequence the stored poster reflects after the fold, and the stored version that holds it. */
export interface FlushOpsResponse {
  opSeq: number;
  jsonVersionId: string;
}

/**
 * What a tab knows about the shared poster: the state the server has confirmed, the sequence it
 * reflects, and this tab's own commits the server has not ordered yet. What the user sees is
 * `confirmed` with `pending` replayed on top (see replayPendingEnvelopes).
 */
export interface LiveEditsState {
  confirmed: SnapshotRecord;
  lastAppliedSeq: number;
  pending: LocalOpEnvelope[];
}

/** How the tab's envelope reaches the server. */
export enum PublishTransport {
  /** The ordinary AJAX call. */
  AJAX = 'ajax',
  /** A keepalive request that survives the page closing. */
  KEEPALIVE = 'keepalive',
}

/** How long the first retry of a failed publish waits; each failure in a row doubles it, up to the cap (the op id makes a retry safe). */
export const PUBLISH_RETRY_BASE_DELAY_MS = 1_000;
export const PUBLISH_RETRY_MAX_DELAY_MS = 30_000;

/** How long a failed read of the log waits before it is tried again. */
export const CATCH_UP_RETRY_DELAY_MS = 2_000;

/**
 * How long a peer's edit sequence may stay ahead of this tab before the log is read. Normally the
 * channel delivers the envelope within this window; a peer's envelope stays off the channel only
 * when it was sent before that peer had noticed this tab.
 */
export const REMOTE_EDIT_SEQ_CATCH_UP_DELAY_MS = 1_000;

/** Browsers cap the bytes in flight for keepalive requests at 64 KB; a bigger envelope has to take the ordinary path. */
export const KEEPALIVE_MAX_BODY_BYTES = 60_000;

/** The server's CSRF double-submit: the token is read from this cookie and posted under the name the page's meta tag gives. */
export const CSRF_COOKIE_NAME = 'csrf_cookie';
export const CSRF_TOKEN_NAME_META = 'data-csrf-name';
export const DEFAULT_CSRF_TOKEN_NAME = 'csrf_token';
export const CSRF_TOKEN_SOURCE_FIELD = 'csrf_token_source';
export const CSRF_TOKEN_SOURCE_COOKIE = '0';

/** The server's answer envelope, as every AJAX endpoint wraps it. */
export interface JsonResponseEnvelope {
  status: string;
  data?: unknown;
  message?: string;
  code?: string | null;
}

/** How often a remote apply held back by a local gesture checks whether the gesture has ended. */
export const DEFERRED_APPLY_POLL_MS = 100;

/** Commits made within this window travel as one envelope while someone else is in the poster: fine-grained enough to feel live. */
export const LIVE_ENVELOPE_WINDOW_MS = 100;

/** Alone, commits are batched longer: nobody is waiting, and fewer envelopes mean fewer writes. */
export const SOLO_ENVELOPE_WINDOW_MS = 1_500;

/** A batch is sent early once it holds this many ops, well under what the server accepts per envelope. */
export const MAX_OPS_PER_ENVELOPE = 150;

/** How long a flush waits for the server to acknowledge every pending envelope before giving up. */
export const FLUSH_TIMEOUT_MS = 15_000;
